import { useCurrentUserSitePermissions } from 'api/UserSiteRoles'
import {
  CUSTOMERS_MANAGE,
  CUSTOMERS_VIEW,
  REPORTS_DOWNLOAD,
  RESERVATIONS_MANAGE,
  RESERVATIONS_VIEW,
  ROTAS_MANAGE,
  SETTINGS_MANAGE,
} from 'constants/permissions'
import { useCurrentSiteId } from 'contexts/SiteConfig'
import { chain } from 'lodash'
import { useCallback, useMemo } from 'react'

export const useCurrentUserCan = () => {
  const siteId = useCurrentSiteId()
  const { data: { data: currentUserRoles } = {} } = useCurrentUserSitePermissions()

  const permissions = useMemo(() => {
    return chain(currentUserRoles)
      .filter(({ site_id: currentSiteId }) => siteId === currentSiteId || currentSiteId === null)
      .map('permissions')
      .flatten()
      .uniq()
      .value()
  }, [currentUserRoles, siteId])

  return useCallback((permission: string) => permissions.indexOf(permission) > -1, [permissions])
}

export const useCurrentUserCanForCurrentSite = () => {
  const siteId = useCurrentSiteId()
  const { data: { data: currentUserRoles } = {} } = useCurrentUserSitePermissions()

  const permissions = chain(currentUserRoles)
    .filter(({ site_id: currentSiteId }) => siteId === currentSiteId)
    .map('permissions')
    .flatten()
    .uniq()
    .value()

  return useCallback(
    (permission: string) => {
      return permissions.indexOf(permission) > -1
    },
    [permissions]
  )
}

export const useCurrentUserCanManageSettingsForCurrentSite = () => {
  const can = useCurrentUserCanForCurrentSite()

  return can(SETTINGS_MANAGE)
}

export const useCurrentUserCanManageRotasForCurrentSite = () => {
  const can = useCurrentUserCanForCurrentSite()

  return can(ROTAS_MANAGE)
}

export const useCurrentUserCanViewReservationsForCurrentSite = () => {
  const can = useCurrentUserCanForCurrentSite()

  return can(RESERVATIONS_VIEW)
}

export const useCurrentUserCanManageReservationsForCurrentSite = () => {
  const can = useCurrentUserCanForCurrentSite()
  return can(RESERVATIONS_MANAGE)
}

export const useCurrentUserCanViewClientsForCurrentSite = () => {
  const can = useCurrentUserCanForCurrentSite()
  return can(CUSTOMERS_VIEW)
}

export const useCurrentUserCanManageClientsForCurrentSite = () => {
  const can = useCurrentUserCanForCurrentSite()
  return can(CUSTOMERS_MANAGE)
}

export const useCurrentUserCanDownloadReportsForCurrentSite = () => {
  const can = useCurrentUserCanForCurrentSite()

  return can(REPORTS_DOWNLOAD)
}

// This will be used to check for permissions for client data, due to this being shared against
// all sites belonging to a brand. Currently, it will return the same as useCurrentUserCanForCurrentSite
// but if it becomes a requirement to check for permissions against the brand in the UI (which is being done in most related API endpoints),
// this hook will be updated to do so, so wherever it's used will automatically have the updated logic.
export const useCurrentUserCanForCurrentBrand = () => {
  return useCurrentUserCanForCurrentSite()
}

export const useCurrentUserCanViewClientsForCurrentBrand = () => {
  const can = useCurrentUserCanForCurrentBrand()

  return can(CUSTOMERS_VIEW)
}

export const withUserHasPermission =
  (Component) =>
  ({ requiredPermission = '', ...props }) => {
    return (
      <IfUserHasPermission requiredPermission={requiredPermission}>
        <Component {...props} />
      </IfUserHasPermission>
    )
  }

export const IfUserHasPermission = ({ requiredPermission, children }) => {
  const can = useCurrentUserCan()

  if (requiredPermission && !can(requiredPermission)) return null

  return children
}
